/**
 * Partner account security (identity; flag identity.passkeys): the signed-in person's passkeys - list, add, rename,
 * remove - over the same /v1/identity/passkeys API as the customer portal (partner passkeys are bound to this host).
 * Routed outside the approval gate so a partner owner in forced 2FA enrolment can enrol with a passkey (identity
 * routes stay open in that state). 401 -> sign-in; 404 (flag off) -> "not available". Minimal wiring with the
 * partner kit; the browser ceremony comes from lib/passkeys. Strings reuse the shared shell.passkeys.* catalog.
 */
import { useState, type FormEvent } from 'react';
import type { PasskeyView } from '@qm/contracts/identity';
import { Button, Text } from '@qm/ui';
import { useSurface } from '../kit/context';
import { errorInfo } from '../kit/http';
import { useResource } from '../kit/resource';
import { Badge, DataTable, ErrorNote, Field, Notice, Screen, When, type Column } from '../kit/ui';
import { passkeyErrorKey, passkeysSupported, registerPasskey } from '../../../lib/passkeys';
import { SignInScreen, usePartnerApi } from './layout';

export function SecurityScreen() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const res = useResource('identity:passkeys', api.passkeys);
  if (res.status === 'loading') return <Text tone="muted">{t('portal.kit.loading')}</Text>;
  if (res.status === 'error') {
    const e = errorInfo(res.error);
    if (e.status === 401) return <SignInScreen />;
    return (
      <Screen title={t('shell.passkeys.title')} testId="partner-security">
        {e.status === 404 || e.code === 'feature_disabled' ? <Notice testId="passkeys-off">{t('portal.kit.featureDisabled')}</Notice> : <ErrorNote error={res.error} />}
      </Screen>
    );
  }
  return <PartnerPasskeys rows={res.data} reload={res.reload} supported={passkeysSupported()} />;
}

export function PartnerPasskeys({ rows, reload, supported }: { rows: PasskeyView[]; reload: () => void; supported: boolean }) {
  const { t } = useSurface();
  const api = usePartnerApi();
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [codes, setCodes] = useState<string[]>([]);

  const act = async (run: () => Promise<unknown>, okKey: string) => {
    setError(null); setStatus(null); setCodes([]);
    try { await run(); setStatus(t(okKey)); setEditing(null); reload(); } catch (err) { setError(t(passkeyErrorKey(err))); }
  };
  // recoveryCodes come back once when this is the person's first recovery set (passkey-only accounts)
  const add = () => act(async () => { const r = await registerPasskey(t('shell.passkeys.defaultName')); if (r.recoveryCodes?.length) setCodes(r.recoveryCodes); }, 'shell.passkeys.added');
  const save = (e: FormEvent) => { e.preventDefault(); if (editing) void act(() => api.renamePasskey(editing.id, editing.name.trim()), 'shell.passkeys.renamed'); };

  const columns: Column<PasskeyView>[] = [
    { key: 'name', header: t('shell.col.name'), render: (r) => <><bdi>{r.name}</bdi>{r.deviceType === 'multiDevice' ? <> <Badge>{t('shell.passkeys.synced')}</Badge></> : null}</> },
    { key: 'createdAt', header: t('shell.col.created'), render: (r) => <When iso={r.createdAt} />, narrow: 'hide' },
    { key: 'lastUsedAt', header: t('shell.passkeys.lastUsed'), render: (r) => (r.lastUsedAt ? <When iso={r.lastUsedAt} /> : t('shell.passkeys.never')) },
    { key: 'actions', header: t('shell.col.action'), render: (r) => (
      <>
        <Button onClick={() => setEditing({ id: r.id, name: r.name })}>{t('shell.passkeys.rename')}</Button>{' '}
        <Button onClick={() => void act(() => api.removePasskey(r.id), 'shell.passkeys.removed')}>{t('shell.passkeys.remove')}</Button>
      </>
    ) },
  ];

  return (
    <Screen title={t('shell.passkeys.title')} subtitle={t('shell.passkeys.intro')} testId="partner-security"
      actions={supported ? <Button variant="primary" onClick={() => void add()} data-testid="partner-passkey-add">{t('shell.passkeys.add')}</Button> : undefined}>
      {!supported ? <Text>{t('shell.passkeys.unsupported')}</Text> : null}
      {status ? <Notice tone="good">{status}</Notice> : null}
      {codes.length ? (
        <Notice testId="partner-passkey-recovery-codes">
          <Text>{t('shell.signin.recoveryTitle')}</Text>
          <Text tone="muted">{t('shell.signin.recoveryIntro')}</Text>
          <ul dir="ltr">{codes.map((rc) => <li key={rc}><code>{rc}</code></li>)}</ul>
        </Notice>
      ) : null}
      {error ? <Text role="alert">{error}</Text> : null}
      {editing ? (
        <form onSubmit={save} noValidate>
          <Field label={t('shell.passkeys.name')}>
            {(id, describedBy) => <input id={id} required maxLength={64} value={editing.name} aria-describedby={describedBy} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />}
          </Field>
          <Button variant="primary" type="submit" disabled={!editing.name.trim()}>{t('shell.passkeys.save')}</Button>{' '}
          <Button type="button" onClick={() => setEditing(null)}>{t('shell.passkeys.cancel')}</Button>
        </form>
      ) : null}
      <DataTable caption={t('shell.passkeys.title')} columns={columns} rows={rows} rowKey={(r) => r.id} empty={t('shell.passkeys.empty')} />
    </Screen>
  );
}
